import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layers, Activity, TrendingUp, AlertTriangle, ArrowUpRight, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useTenant } from "../lib/tenant";

const SLA_COLOR = {
    verde: "text-[color:var(--brand-exito)] bg-[color:var(--brand-exito)]/10 border-[color:var(--brand-exito)]/40",
    gold: "text-[color:var(--brand-dark-gold)] bg-[color:var(--brand-gold)]/20 border-[color:var(--brand-dark-gold)]/40",
    rojo: "text-[color:var(--brand-coral)] bg-[color:var(--brand-coral)]/10 border-[color:var(--brand-coral)]/40",
};

export default function AdminDashboardOperacion() {
    const { switchTenant, activeTenantId } = useTenant();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/dashboard-operacion");
                setData(data);
            } catch (err) {
                toast.error("Error cargando dashboard");
            } finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <div className="p-10 text-sm text-muted-foreground">Cargando…</div>;
    if (!data) return null;

    const { resumen, tenants } = data;

    return (
        <div className="p-8 lg:p-10 space-y-8 max-w-[1600px]" data-testid="admin-dashboard-operacion">
            <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                    Multi-tenant · Panel operativo
                </div>
                <h1 className="font-display text-4xl tracking-tight">
                    Trafalgar como operador · {resumen.tenants_totales} tenants
                </h1>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                    Vista cruzada de todos los clientes fiduciarios que Trafalgar opera.
                    Click en cualquier fila para operar como ese tenant (impersonation con
                    trazabilidad en bitácora).
                </p>
            </div>

            {/* Resumen */}
            <div className="grid md:grid-cols-4 gap-4">
                <div className="kpi-card" data-testid="kpi-tenants-totales">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Layers className="h-3.5 w-3.5" /> Tenants totales
                    </div>
                    <div className="kpi-value mt-2">{resumen.tenants_totales}</div>
                    <div className="mt-2 text-xs text-muted-foreground">
                        <b className="text-[color:var(--brand-exito)]">{resumen.tenants_activos}</b> activos
                        · <b className="text-[color:var(--brand-coral)]">{resumen.tenants_suspendidos}</b> suspendidos
                    </div>
                </div>
                <div className="kpi-card" data-testid="kpi-fideicomisos-totales">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <Activity className="h-3.5 w-3.5" /> Fideicomisos globales
                    </div>
                    <div className="kpi-value mt-2">{resumen.fideicomisos_totales}</div>
                    <div className="text-xs text-muted-foreground mt-1">bajo administración</div>
                </div>
                <div className="kpi-card" data-testid="kpi-obligaciones-vencidas">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <AlertTriangle className="h-3.5 w-3.5" /> Obligaciones vencidas
                    </div>
                    <div className={`kpi-value mt-2 ${resumen.obligaciones_vencidas_totales > 0 ? "text-[color:var(--brand-coral)]" : ""}`}>
                        {resumen.obligaciones_vencidas_totales}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">Cross-tenant</div>
                </div>
                <div className="kpi-card">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-widest">
                        <TrendingUp className="h-3.5 w-3.5" /> Acciones
                    </div>
                    <div className="mt-3">
                        <Link to="/admin/tenants" data-testid="link-admin-tenants">
                            <div className="inline-flex items-center gap-1 text-sm text-[color:var(--brand-dark-gold)] hover:underline">
                                Gestionar tenants <ArrowUpRight className="h-3 w-3" />
                            </div>
                        </Link>
                    </div>
                </div>
            </div>

            {/* Tabla tenants */}
            <div className="rounded-md border border-border bg-card overflow-hidden">
                <div className="px-5 py-3 border-b border-border">
                    <h2 className="font-display text-lg">Tenants bajo operación</h2>
                    <p className="text-[11px] text-muted-foreground">
                        Métricas separadas por cliente. SLA basado en incidentes vs. límite mensual.
                    </p>
                </div>
                <table className="trf-table">
                    <thead><tr>
                        <th>Tenant</th>
                        <th>Estado</th>
                        <th className="text-right">Fideicomisos</th>
                        <th className="text-right">Activos</th>
                        <th className="text-right">Oblig. venc.</th>
                        <th className="text-right">Bitácora</th>
                        <th>Última acción</th>
                        <th>SLA</th>
                        <th></th>
                    </tr></thead>
                    <tbody>
                        {tenants.map(t => {
                            const slaClass = SLA_COLOR[t.sla_estado] || SLA_COLOR.verde;
                            return (
                                <tr key={t.tenant_id} data-testid={`row-tenant-${t.slug}`}>
                                    <td>
                                        <div className="font-medium flex items-center gap-1.5">
                                            <Layers className="h-3.5 w-3.5 text-[color:var(--brand-dark-gold)]" />
                                            {t.slug}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {t.nombre_comercial}
                                        </div>
                                    </td>
                                    <td>
                                        <span className={t.estado === "activo" ? "badge-exito" : "badge-error"}>
                                            {t.estado}
                                        </span>
                                    </td>
                                    <td className="text-right tabular font-medium">{t.fideicomisos_totales}</td>
                                    <td className="text-right tabular text-[color:var(--brand-exito)]">
                                        {t.fideicomisos_activos}
                                    </td>
                                    <td className={`text-right tabular ${t.obligaciones_vencidas > 0 ? "text-[color:var(--brand-coral)] font-bold" : ""}`}>
                                        {t.obligaciones_vencidas}
                                    </td>
                                    <td className="text-right tabular text-xs">{t.bitacora_registros}</td>
                                    <td className="text-[10px] text-muted-foreground tabular">
                                        {t.ultima_actividad?.substring(0, 16) || "—"}
                                    </td>
                                    <td>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-medium uppercase ${slaClass}`}>
                                            {t.sla_estado} ({(t.sla_ratio * 100).toFixed(0)}%)
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => switchTenant(t.tenant_id)}
                                            className="text-xs text-[color:var(--brand-dark-gold)] hover:underline"
                                            data-testid={`btn-operate-${t.slug}`}
                                        >
                                            Operar como →
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
